'use client';
import { createClient } from '@/lib/supabase/client';
import { sendDisputeEscalationEmail } from '@/lib/email/transactional';

// ── Types ─────────────────────────────────────────────────────────────────────

export interface VerificationRequest {
  id: string;
  userId: string;
  requestedTier: string;
  fullLegalName: string;
  institutionalAffiliation: string | null;
  roleDescription: string;
  idDocumentUrl: string | null;
  supportingDocUrl: string | null;
  notes: string | null;
  status: 'pending' | 'under_review' | 'approved' | 'rejected';
  reviewedBy: string | null;
  reviewedAt: string | null;
  reviewerNotes: string | null;
  createdAt: string;
}

export interface ContributionVersion {
  id: string;
  contributionId: string;
  versionNumber: number;
  title: string;
  body: string;
  excerpt: string | null;
  certaintyLabel: string;
  region: string | null;
  changeSummary: string | null;
  editedBy: string | null;
  editorName: string | null;
  createdAt: string;
}

export interface DisputeCase {
  id: string;
  contributionId: string;
  raisedBy: string;
  raisedByName: string | null;
  disputeType: string;
  claim: string;
  evidence: string | null;
  status: 'open' | 'under_review' | 'resolved_upheld' | 'resolved_dismissed' | 'escalated';
  assignedTo: string | null;
  resolutionNotes: string | null;
  resolvedBy: string | null;
  resolvedAt: string | null;
  createdAt: string;
  responses?: DisputeResponse[];
}

export interface DisputeResponse {
  id: string;
  disputeId: string;
  authorId: string;
  authorName: string | null;
  authorAvatar: string | null;
  body: string;
  isOfficial: boolean;
  createdAt: string;
}

export interface ContentWarning {
  id: string;
  contributionId: string;
  label: 'trauma' | 'colonial_violence' | 'genocide' | 'graphic_content' | 'cultural_sensitivity';
  description: string | null;
  addedBy: string | null;
  createdAt: string;
}

// ── Verification Requests ─────────────────────────────────────────────────────

export async function submitVerificationRequest(data: {
  fullLegalName: string;
  institutionalAffiliation?: string;
  roleDescription: string;
  notes?: string;
}): Promise<{ id: string } | null> {
  const supabase = createClient();
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return null;

  const { data: result, error } = await supabase
    .from('verification_requests')
    .insert({
      user_id: user.id,
      requested_tier: 'historian',
      full_legal_name: data.fullLegalName,
      institutional_affiliation: data.institutionalAffiliation || null,
      role_description: data.roleDescription,
      notes: data.notes || null,
    })
    .select('id')
    .single();

  if (error) { console.error('submitVerificationRequest:', error.message); return null; }
  return result;
}

export async function getMyVerificationRequest(): Promise<VerificationRequest | null> {
  const supabase = createClient();
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return null;

  const { data, error } = await supabase
    .from('verification_requests')
    .select('*')
    .eq('user_id', user.id)
    .order('created_at', { ascending: false })
    .limit(1)
    .single();

  if (error || !data) return null;
  return mapVerificationRequest(data);
}

export async function getAllVerificationRequests(): Promise<VerificationRequest[]> {
  const supabase = createClient();
  const { data, error } = await supabase
    .from('verification_requests')
    .select('*')
    .order('created_at', { ascending: false });

  if (error) { console.error('getAllVerificationRequests:', error.message); return []; }
  return (data || []).map(mapVerificationRequest);
}

export async function reviewVerificationRequest(
  id: string,
  status: 'approved' | 'rejected' | 'under_review',
  reviewerNotes?: string
): Promise<boolean> {
  const supabase = createClient();
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return false;

  const { error } = await supabase
    .from('verification_requests')
    .update({
      status,
      reviewed_by: user.id,
      reviewed_at: new Date().toISOString(),
      reviewer_notes: reviewerNotes || null,
    })
    .eq('id', id);

  if (error) { console.error('reviewVerificationRequest:', error.message); return false; }

  // If approved, upgrade the user's tier
  if (status === 'approved') {
    const { data: req } = await supabase
      .from('verification_requests')
      .select('user_id, requested_tier')
      .eq('id', id)
      .single();
    if (req) {
      await supabase
        .from('user_profiles')
        .update({ tier: req.requested_tier, role: 'historian' })
        .eq('id', req.user_id);
    }
  }
  return true;
}

function mapVerificationRequest(d: any): VerificationRequest {
  return {
    id: d.id,
    userId: d.user_id,
    requestedTier: d.requested_tier,
    fullLegalName: d.full_legal_name,
    institutionalAffiliation: d.institutional_affiliation,
    roleDescription: d.role_description,
    idDocumentUrl: d.id_document_url,
    supportingDocUrl: d.supporting_doc_url,
    notes: d.notes,
    status: d.status,
    reviewedBy: d.reviewed_by,
    reviewedAt: d.reviewed_at,
    reviewerNotes: d.reviewer_notes,
    createdAt: d.created_at,
  };
}

// ── Story Versions ────────────────────────────────────────────────────────────

export async function getContributionVersions(contributionId: string): Promise<ContributionVersion[]> {
  const supabase = createClient();
  const { data, error } = await supabase
    .from('contribution_versions')
    .select('*, user_profiles!edited_by(full_name)')
    .eq('contribution_id', contributionId)
    .order('version_number', { ascending: false });

  if (error) { console.error('getContributionVersions:', error.message); return []; }
  return (data || []).map((d: any) => ({
    id: d.id,
    contributionId: d.contribution_id,
    versionNumber: d.version_number,
    title: d.title,
    body: d.body,
    excerpt: d.excerpt,
    certaintyLabel: d.certainty_label,
    region: d.region,
    changeSummary: d.change_summary,
    editedBy: d.edited_by,
    editorName: d.user_profiles?.full_name || null,
    createdAt: d.created_at,
  }));
}

export async function saveContributionVersion(data: {
  contributionId: string;
  title: string;
  body: string;
  excerpt?: string;
  certaintyLabel: string;
  region?: string;
  changeSummary: string;
}): Promise<boolean> {
  const supabase = createClient();
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return false;

  // Get current max version number
  const { data: maxVer } = await supabase
    .from('contribution_versions')
    .select('version_number')
    .eq('contribution_id', data.contributionId)
    .order('version_number', { ascending: false })
    .limit(1)
    .single();

  const nextVersion = (maxVer?.version_number || 0) + 1;

  const { error } = await supabase
    .from('contribution_versions')
    .insert({
      contribution_id: data.contributionId,
      version_number: nextVersion,
      title: data.title,
      body: data.body,
      excerpt: data.excerpt || null,
      certainty_label: data.certaintyLabel,
      region: data.region || null,
      change_summary: data.changeSummary,
      edited_by: user.id,
    });

  if (error) { console.error('saveContributionVersion:', error.message); return false; }
  return true;
}

// ── Dispute Cases ─────────────────────────────────────────────────────────────

export async function getDisputesForContribution(contributionId: string): Promise<DisputeCase[]> {
  const supabase = createClient();
  const { data, error } = await supabase
    .from('dispute_cases')
    .select('*, user_profiles!raised_by(full_name)')
    .eq('contribution_id', contributionId)
    .order('created_at', { ascending: false });

  if (error) { console.error('getDisputesForContribution:', error.message); return []; }
  return (data || []).map(mapDisputeCase);
}

export async function getAllDisputes(): Promise<DisputeCase[]> {
  const supabase = createClient();
  const { data, error } = await supabase
    .from('dispute_cases')
    .select('*, user_profiles!raised_by(full_name)')
    .order('created_at', { ascending: false });

  if (error) { console.error('getAllDisputes:', error.message); return []; }
  return (data || []).map(mapDisputeCase);
}

export async function createDisputeCase(data: {
  contributionId: string;
  disputeType: string;
  claim: string;
  evidence?: string;
}): Promise<{ id: string } | null> {
  const supabase = createClient();
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return null;

  const { data: result, error } = await supabase
    .from('dispute_cases')
    .insert({
      contribution_id: data.contributionId,
      raised_by: user.id,
      dispute_type: data.disputeType,
      claim: data.claim,
      evidence: data.evidence || null,
    })
    .select('id')
    .single();

  if (error) { console.error('createDisputeCase:', error.message); return null; }

  // Notify the contribution author via email
  if (result) {
    try {
      const { data: contrib } = await supabase
        .from('contributions')
        .select('title, author_id')
        .eq('id', data.contributionId)
        .maybeSingle();

      if (contrib?.author_id && contrib.author_id !== user.id) {
        const { data: disputerProfile } = await supabase
          .from('user_profiles')
          .select('full_name, username')
          .eq('id', user.id)
          .maybeSingle();
        const disputerName = disputerProfile?.full_name || disputerProfile?.username || 'A community member';

        await sendDisputeEscalationEmail({
          recipientUserId: contrib.author_id,
          contributionTitle: contrib.title,
          contributionId: data.contributionId,
          disputeType: data.disputeType,
          disputerName,
          claim: data.claim,
        });
      }
    } catch (emailErr: any) {
      console.error('createDisputeCase email error:', emailErr.message);
    }
  }

  return result;
}

export async function updateDisputeStatus(
  id: string,
  status: DisputeCase['status'],
  resolutionNotes?: string
): Promise<boolean> {
  const supabase = createClient();
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return false;

  const updates: any = { status };
  if (resolutionNotes) updates.resolution_notes = resolutionNotes;
  if (['resolved_upheld', 'resolved_dismissed'].includes(status)) {
    updates.resolved_by = user.id;
    updates.resolved_at = new Date().toISOString();
  }

  const { error } = await supabase
    .from('dispute_cases')
    .update(updates)
    .eq('id', id);

  if (error) { console.error('updateDisputeStatus:', error.message); return false; }
  return true;
}

export async function addDisputeResponse(data: {
  disputeId: string;
  body: string;
  isOfficial?: boolean;
}): Promise<boolean> {
  const supabase = createClient();
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return false;

  const { error } = await supabase
    .from('dispute_responses')
    .insert({
      dispute_id: data.disputeId,
      author_id: user.id,
      body: data.body,
      is_official: data.isOfficial || false,
    });

  if (error) { console.error('addDisputeResponse:', error.message); return false; }
  return true;
}

export async function getDisputeResponses(disputeId: string): Promise<DisputeResponse[]> {
  const supabase = createClient();
  const { data, error } = await supabase
    .from('dispute_responses')
    .select('*, user_profiles!author_id(full_name, avatar_url)')
    .eq('dispute_id', disputeId)
    .order('created_at', { ascending: true });

  if (error) { console.error('getDisputeResponses:', error.message); return []; }
  return (data || []).map((d: any) => ({
    id: d.id,
    disputeId: d.dispute_id,
    authorId: d.author_id,
    authorName: d.user_profiles?.full_name || null,
    authorAvatar: d.user_profiles?.avatar_url || null,
    body: d.body,
    isOfficial: d.is_official,
    createdAt: d.created_at,
  }));
}

function mapDisputeCase(d: any): DisputeCase {
  return {
    id: d.id,
    contributionId: d.contribution_id,
    raisedBy: d.raised_by,
    raisedByName: d.user_profiles?.full_name || null,
    disputeType: d.dispute_type,
    claim: d.claim,
    evidence: d.evidence,
    status: d.status,
    assignedTo: d.assigned_to,
    resolutionNotes: d.resolution_notes,
    resolvedBy: d.resolved_by,
    resolvedAt: d.resolved_at,
    createdAt: d.created_at,
  };
}

// ── Content Warnings ──────────────────────────────────────────────────────────

export async function getContentWarnings(contributionId: string): Promise<ContentWarning[]> {
  const supabase = createClient();
  const { data, error } = await supabase
    .from('content_warnings')
    .select('*')
    .eq('contribution_id', contributionId);

  if (error) { console.error('getContentWarnings:', error.message); return []; }
  return (data || []).map((d: any) => ({
    id: d.id,
    contributionId: d.contribution_id,
    label: d.label,
    description: d.description,
    addedBy: d.added_by,
    createdAt: d.created_at,
  }));
}

export async function addContentWarning(data: {
  contributionId: string;
  label: ContentWarning['label'];
  description?: string;
}): Promise<boolean> {
  const supabase = createClient();
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return false;

  const { error } = await supabase
    .from('content_warnings')
    .insert({
      contribution_id: data.contributionId,
      label: data.label,
      description: data.description || null,
      added_by: user.id,
    });

  if (error && !error.message.includes('unique')) {
    console.error('addContentWarning:', error.message);
    return false;
  }
  return true;
}

export async function removeContentWarning(id: string): Promise<boolean> {
  const supabase = createClient();
  const { error } = await supabase
    .from('content_warnings')
    .delete()
    .eq('id', id);

  if (error) { console.error('removeContentWarning:', error.message); return false; }
  return true;
}
